/**
 * Atajos del concepto de un cobro: la lista que ofrece el desplegable y, si
 * el concepto tiene importe fijo, el importe que se rellena solo al elegirlo
 * (concepto e importe siguen siendo editables).
 *
 * Los conceptos y sus importes son de CADA clínica: viven en Configuración
 * (`cobro_presets` de GET /api/settings, una lista de {concepto, importe}; el
 * importe vacío es «importe manual»). No hay lista de respaldo en el código:
 * sin configuración, el desplegable solo ofrece «Devolución» y el concepto se
 * escribe a mano.
 *
 * «Devolución» sí es del programa, no de la clínica: sirve para apuntar a mano
 * una devolución desde Cobros, aparte de las que vuelca solas «Facturas y
 * devoluciones». El importe se escribe en positivo y se guarda en negativo,
 * sin IVA.
 */
import { useEffect, useState } from 'react';
import { api } from './api';

export const DEVOLUCION_CONCEPT = 'Devolución';

const DEVOLUCION_PRESET = { value: DEVOLUCION_CONCEPT, amountLabel: 'Importe manual', devolucion: true };

const normalize = (value) => (value || '')
  .trim()
  .toLocaleLowerCase('es-ES')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '');

/** ¿Es una devolución? «Devolución», «devolucion — reserva», «DEVOLUCIÓN cobro duplicado»… */
export function isDevolucionConcept(value) {
  return normalize(value).startsWith('devolucion');
}

/**
 * Lo guardado en Configuración → la forma que usan los formularios
 * ({value, amount?, amountLabel}). Lo que no tiene concepto se descarta; un
 * importe que no es un número positivo cuenta como «importe manual». Una
 * «devolución» configurada se ignora: ya la pone el programa, al final.
 */
export function normalizarCobroPresets(crudo) {
  if (!Array.isArray(crudo)) return [];
  const vistos = new Set();
  const lista = [];
  for (const item of crudo) {
    const value = String(item?.concepto ?? item?.value ?? '').trim();
    const clave = normalize(value);
    if (!value || vistos.has(clave) || isDevolucionConcept(value)) continue;
    vistos.add(clave);
    const crudoImporte = item?.importe ?? item?.amount;
    const importe = crudoImporte === '' || crudoImporte == null ? NaN : Number(crudoImporte);
    lista.push(Number.isFinite(importe) && importe > 0
      ? { value, amount: importe, amountLabel: `${importe} €` }
      : { value, amountLabel: 'Importe manual' });
  }
  return lista;
}

// Los de la clínica, tal y como llegaron la última vez. Las funciones de abajo
// los usan si no se les pasa una lista; `useCobroPresets` los recarga al
// montar (un cambio en Configuración se ve al abrir el siguiente cobro).
let cargados = [];
const oyentes = new Set();

function fijar(lista) {
  cargados = lista;
  oyentes.forEach((f) => f(lista));
}

/** Pide los atajos al servidor. Un fallo deja los que había (o ninguno). */
export async function cargarCobroPresets() {
  try {
    const r = await api.get('/api/settings');
    fijar(normalizarCobroPresets(r?.data?.cobro_presets));
  } catch {
    // Sin ajustes no hay atajos: el concepto se escribe a mano.
  }
  return cobroConceptPresets();
}

/** Los atajos de la clínica y, al final, «Devolución». */
export function cobroConceptPresets(lista = cargados) {
  return [...lista, DEVOLUCION_PRESET];
}

/** Hook: los atajos (con «Devolución»), recargados al montar. */
export function useCobroPresets() {
  const [lista, setLista] = useState(cargados);
  useEffect(() => {
    oyentes.add(setLista);
    cargarCobroPresets();
    return () => { oyentes.delete(setLista); };
  }, []);
  return cobroConceptPresets(lista);
}

export function findCobroConceptPreset(value, presets = cobroConceptPresets()) {
  const normalized = normalize(value);
  return presets.find((preset) => normalize(preset.value) === normalized) || null;
}

export function canonicalCobroConcept(value, presets = cobroConceptPresets()) {
  return findCobroConceptPreset(value, presets)?.value || (value || '').trim();
}

/**
 * Importes rápidos para un concepto: primero el suyo (si lo tiene) y después
 * los importes fijos de los demás atajos, de menor a mayor. Sin atajos, ninguno.
 */
export function amountOptionsForConcept(value, presets = cobroConceptPresets()) {
  const preset = findCobroConceptPreset(value, presets);
  const preferred = preset?.amounts || (preset?.amount != null ? [preset.amount] : []);
  const todos = presets.map((p) => p.amount).filter((a) => a != null).sort((a, b) => a - b);
  return [...new Set([...preferred, ...todos])];
}

/**
 * Devuelve el importe que debe aplicarse al elegir un concepto exacto.
 * `null` indica que el concepto es libre; `''`, que el concepto existe pero
 * requiere que la persona elija o escriba el importe.
 */
export function suggestedAmountForConcept(value, presets = cobroConceptPresets()) {
  const preset = findCobroConceptPreset(value, presets);
  if (!preset) return null;
  return preset.amount != null ? String(preset.amount) : '';
}
